"use client"

import React, { useState, useRef, useEffect } from 'react'
import Image from 'next/image'
import { ChevronDown, Menu, X, LayoutGrid, Handshake, Info, Mail, Activity, BookOpen, LayoutDashboard, Cpu, type LucideIcon } from 'lucide-react'
import { HiOutlineSquares2X2 } from 'react-icons/hi2'
import { MdOutlineGavel, MdOutlinePrivacyTip, MdOutlinePayments, MdOutlineCookie } from 'react-icons/md'
import { FaDiscord, FaTiktok, FaInstagram, FaYoutube } from 'react-icons/fa6'
import LanguageSwitcher from '@/app/[locale]/Components/LanguageSwitcher'

import { useTranslations } from 'next-intl'

const Navbar = () => {
    const t = useTranslations('Navbar')
    const [isBannerVisible, setIsBannerVisible] = useState(false)
    const [isProductsOpen, setIsProductsOpen] = useState(false)
    const [isSupportOpen, setIsSupportOpen] = useState(false)
    const [isSocialOpen, setIsSocialOpen] = useState(false)
    const [isAboutOpen, setIsAboutOpen] = useState(false)
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
    const [isScrolled, setIsScrolled] = useState(false)

    const productsTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const supportTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const socialTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const aboutTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    /** `isCustom` entries render `Icon` on a solid tile instead of a game logo. */
    type ProductItem = {
        name: string
        href: string
        description: string
        logo: string
        isCustom: boolean
        Icon?: LucideIcon
    }

    const productsDropdown: ProductItem[] = [
        { name: "Minecraft Servers", href: "/minecraft-servers", description: t('desc_mc'), logo: "https://cdn.aurex.lk/games/mc-logo.webp", isCustom: false },
        { name: "Hytale Servers", href: "/hytale-servers", description: t('desc_hytale'), logo: "https://cdn.aurex.lk/games/hytale.webp", isCustom: false },
        { name: "MTA Servers", href: "/mta-servers", description: t('desc_mta'), logo: "https://cdn.aurex.lk/games/mta.webp", isCustom: false },
        { name: "FiveM Servers", href: "/fivem-servers", description: t('desc_fivem'), logo: "https://cdn.aurex.lk/fivem/fm-left.webp", isCustom: false },
        { name: "Rust Servers", href: "/rust-servers", description: t('desc_rust'), logo: "https://cdn.aurex.lk/rust/rust-left.webp", isCustom: false },
        { name: t('other_games'), href: "/games", description: t('desc_other'), logo: "", isCustom: true, Icon: LayoutGrid },
    ]

    const supportDropdown = [
        { name: "Terms Of Service", href: "/tos", description: t('desc_tos'), Icon: MdOutlineGavel },
        { name: "Privacy Policy", href: "/privacy", description: t('desc_privacy'), Icon: MdOutlinePrivacyTip },
        { name: "Refund Policy", href: "/refund-policy", description: t('desc_refund'), Icon: MdOutlinePayments },
        { name: "Cookie Policy", href: "/cookie-policy", description: t('desc_cookie'), Icon: MdOutlineCookie },
        { name: t('partner'), href: "/partner-agreement", description: t('desc_partner'), Icon: Handshake },
    ]

    const socialDropdown = [
        { name: "Discord", href: "https://discord.com/invite/aurex", description: t('desc_discord'), Icon: FaDiscord, colorClass: "text-[#5865F2]" },
        { name: "TikTok", href: "https://www.tiktok.com/@aurex.lk", description: t('desc_tiktok'), Icon: FaTiktok, colorClass: "text-white" },
        { name: "Instagram", href: "https://www.instagram.com/aurex.lk", description: t('desc_ig'), Icon: FaInstagram, colorClass: "text-pink-500" },
        { name: "YouTube", href: "https://www.youtube.com/@aurexhosting", description: t('desc_yt'), Icon: FaYoutube, colorClass: "text-red-500" },
    ]

    const aboutDropdown = [
        { name: t('about'), href: "/about-us", description: t('desc_about'), Icon: Info, isExternal: false },
        { name: t('game_panel'), href: "/game-panel", description: t('desc_game_panel'), Icon: LayoutDashboard, isExternal: false },
        { name: t('hardware_locations'), href: "/hardware", description: t('desc_hardware_locations'), Icon: Cpu, isExternal: false },
        { name: "Contact", href: "/contact-us", description: t('desc_contact'), Icon: Mail, isExternal: false },
        { name: "Blog", href: "https://dash.aurex.lk/blog", description: t('desc_blog'), Icon: BookOpen, isExternal: true },
    ]

    const handleMouseEnter = (type: 'products' | 'support' | 'social' | 'about') => {
        // Clear all timeouts immediately
        clearTimeout(productsTimeoutRef.current!)
        clearTimeout(supportTimeoutRef.current!)
        clearTimeout(socialTimeoutRef.current!)
        clearTimeout(aboutTimeoutRef.current!)

        // Close all dropdowns
        setIsProductsOpen(false)
        setIsSupportOpen(false)
        setIsSocialOpen(false)
        setIsAboutOpen(false)

        // Open only the hovered one
        if (type === 'products') {
            setIsProductsOpen(true)
        } else if (type === 'support') {
            setIsSupportOpen(true)
        } else if (type === 'social') {
            setIsSocialOpen(true)
        } else {
            setIsAboutOpen(true)
        }
    }

    const handleMouseLeave = (type: 'products' | 'support' | 'social' | 'about') => {
        if (type === 'products') {
            productsTimeoutRef.current = setTimeout(() => setIsProductsOpen(false), 200)
        } else if (type === 'support') {
            supportTimeoutRef.current = setTimeout(() => setIsSupportOpen(false), 200)
        } else if (type === 'social') {
            socialTimeoutRef.current = setTimeout(() => setIsSocialOpen(false), 200)
        } else {
            aboutTimeoutRef.current = setTimeout(() => setIsAboutOpen(false), 200)
        }
    }

    useEffect(() => {
        return () => {
            if (productsTimeoutRef.current) clearTimeout(productsTimeoutRef.current)
            if (supportTimeoutRef.current) clearTimeout(supportTimeoutRef.current)
            if (socialTimeoutRef.current) clearTimeout(socialTimeoutRef.current)
            if (aboutTimeoutRef.current) clearTimeout(aboutTimeoutRef.current)
        }
    }, [])

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 10)
        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    return (
        <div className={`sticky top-0 z-[99999999] font-poppins transition-all duration-300 ${isScrolled ? 'backdrop-blur-xl bg-black/10 border-b border-[#4d0da079]/50 ' : 'border border-transparent'}`}>
            {isBannerVisible && (
                <div className="bg-[#8B5CF6] py-1 text-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex items-center justify-center py-2.5 relative">
                            <p className="text-[10px] sm:text-xs font-medium font-poppins text-center">
                                <span className="">{t('limited')}</span> {t('discount')}{' '}
                                <span className="font-mono bg-white/20 px-1.5 py-0.5 rounded font-bold">WELCOME20</span>
                            </p>
                            <button
                                onClick={() => setIsBannerVisible(false)}
                                className="absolute right-0 text-white/70 hover:text-white transition-colors p-1"
                                aria-label="Close banner"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <nav className="py-2 ">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-14">

                        <a href="/">
                            <div className="flex items-center gap-2.5 flex-shrink-0">
                                <Image
                                    src="https://cdn.aurex.lk/assets/image.webp"
                                    alt="Aurex Hosting"
                                    width={200}
                                    height={64}
                                    priority
                                    className="h-10 sm:h-16 w-auto"
                                    style={{ width: "auto" }}
                                />
                                <span className="text-white text-xl sm:text-2xl font-medium font-poppins whitespace-nowrap">Aurex Hosting</span>
                            </div>
                        </a>

                        <div className="hidden md:flex items-center gap-4 ml-auto">
                            <div className="flex items-center gap-1">
                                <div
                                    className="relative"
                                    onMouseEnter={() => handleMouseEnter('products')}
                                    onMouseLeave={() => handleMouseLeave('products')}
                                >
                                    <button className="flex items-center gap-1 px-3 py-2 text-base text-gray-400 hover:text-white font-quicksand transition-colors">
                                        {t('games')}
                                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isProductsOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    <div
                                        className="absolute top-full left-0 mt-4 w-max bg-[#0d0616]/95 border-2 border-purple-600/40 rounded-3xl z-50 transition-all duration-150 shadow-xl shadow-black/60"
                                        style={{ opacity: isProductsOpen ? 1 : 0, visibility: isProductsOpen ? 'visible' : 'hidden', pointerEvents: isProductsOpen ? 'auto' : 'none' }}
                                        onMouseEnter={() => handleMouseEnter('products')}
                                        onMouseLeave={() => handleMouseLeave('products')}
                                    >
                                        <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            {productsDropdown.map((item, i) => {
                                                const ItemIcon = item.Icon ?? LayoutGrid
                                                return (
                                                <a
                                                    key={i}
                                                    href={item.href}
                                                    className="flex items-center gap-3 px-4 py-4 rounded-2xl hover:bg-white/5 transition-colors group"
                                                >
                                                    {item.isCustom ? (
                                                        <div className="w-[4.5rem] h-[5.5rem] rounded-xl flex-shrink-0 bg-[#8B5CF6] flex items-center justify-center shadow-lg">
                                                            <ItemIcon className="w-8 h-8 text-white" />
                                                        </div>
                                                    ) : (
                                                        <Image src={item.logo} alt={item.name} width={72} height={88} className="w-[4.5rem] h-[5.5rem] object-cover rounded-xl flex-shrink-0 shadow-lg" />
                                                    )}
                                                    <div className="flex flex-col gap-0.5">
                                                        <span className="text-lg text-white font-poppins group-hover:text-[#8B5CF6] transition-colors whitespace-nowrap">{item.name}</span>
                                                        <span className="text-md text-gray-500 font-quicksand whitespace-nowrap">{item.description}</span>
                                                    </div>
                                                </a>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </div>

                                <div
                                    className="relative"
                                    onMouseEnter={() => handleMouseEnter('social')}
                                    onMouseLeave={() => handleMouseLeave('social')}
                                >
                                    <button className="flex items-center gap-1 px-3 py-2 text-base text-gray-400 hover:text-white font-quicksand transition-colors">
                                        {t('social')}
                                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isSocialOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    <div
                                        className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-max bg-[#0d0616]/95 border-2 border-purple-600/40 rounded-3xl z-50 transition-all duration-150 shadow-xl shadow-black/60"
                                        style={{ opacity: isSocialOpen ? 1 : 0, visibility: isSocialOpen ? 'visible' : 'hidden', pointerEvents: isSocialOpen ? 'auto' : 'none' }}
                                        onMouseEnter={() => handleMouseEnter('social')}
                                        onMouseLeave={() => handleMouseLeave('social')}
                                    >
                                        <div className="p-2 flex flex-col gap-1">
                                            {socialDropdown.map((item, i) => (
                                                <a
                                                    key={i}
                                                    href={item.href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-3 px-3 py-2.5 rounded-2xl hover:bg-white/5 transition-colors group"
                                                >
                                                    <item.Icon className={`${item.colorClass} shrink-0 opacity-80 group-hover:opacity-100 transition-opacity`} size={20} />
                                                    <div className="flex flex-col gap-0.5">
                                                        <span className="text-sm text-white font-medium font-poppins group-hover:text-[#8B5CF6] transition-colors whitespace-nowrap">{item.name}</span>
                                                        <span className="text-xs text-gray-400 font-quicksand whitespace-nowrap">{item.description}</span>
                                                    </div>
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div
                                    className="relative"
                                    onMouseEnter={() => handleMouseEnter('about')}
                                    onMouseLeave={() => handleMouseLeave('about')}
                                >
                                    <button className="flex items-center gap-1 px-3 py-2 text-base text-gray-400 hover:text-white font-quicksand transition-colors">
                                        {t('about')}
                                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isAboutOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    <div
                                        className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-max bg-[#0d0616]/95 border-2 border-purple-600/40 rounded-3xl z-50 transition-all duration-150 shadow-xl shadow-black/60"
                                        style={{ opacity: isAboutOpen ? 1 : 0, visibility: isAboutOpen ? 'visible' : 'hidden', pointerEvents: isAboutOpen ? 'auto' : 'none' }}
                                        onMouseEnter={() => handleMouseEnter('about')}
                                        onMouseLeave={() => handleMouseLeave('about')}
                                    >
                                        <div className="p-2 flex flex-col gap-1">
                                            {aboutDropdown.map((item, i) => (
                                                <a
                                                    key={i}
                                                    href={item.href}
                                                    target={item.isExternal ? "_blank" : undefined}
                                                    rel={item.isExternal ? "noopener noreferrer" : undefined}
                                                    className="flex items-center gap-3 px-3 py-2.5 rounded-2xl hover:bg-white/5 transition-colors group"
                                                >
                                                    <item.Icon className="text-purple-400 shrink-0" size={20} />
                                                    <div className="flex flex-col gap-0.5">
                                                        <span className="text-sm text-white font-medium font-poppins group-hover:text-[#8B5CF6] transition-colors whitespace-nowrap">{item.name}</span>
                                                        <span className="text-xs text-gray-400 font-quicksand whitespace-nowrap">{item.description}</span>
                                                    </div>
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <a href="https://status.aurex.lk/" target="_blank" rel="noopener noreferrer" className="px-3 py-2 text-base text-gray-400 hover:text-white font-quicksand transition-colors">
                                    {t('status')}
                                </a>

                                <div
                                    className="relative"
                                    onMouseEnter={() => handleMouseEnter('support')}
                                    onMouseLeave={() => handleMouseLeave('support')}
                                >
                                    <button className="flex items-center gap-1 px-3 py-2 text-base text-gray-400 hover:text-white font-quicksand transition-colors">
                                        {t('legal')}
                                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isSupportOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    <div
                                        className="absolute top-full right-0 mt-4 w-max bg-[#0d0616]/95 border-2 border-purple-600/40 rounded-3xl z-50 transition-all duration-150 shadow-xl shadow-black/60"
                                        style={{ opacity: isSupportOpen ? 1 : 0, visibility: isSupportOpen ? 'visible' : 'hidden', pointerEvents: isSupportOpen ? 'auto' : 'none' }}
                                        onMouseEnter={() => handleMouseEnter('support')}
                                        onMouseLeave={() => handleMouseLeave('support')}
                                    >
                                        <div className="p-2 flex flex-col gap-1">
                                            {supportDropdown.map((item, i) => (
                                                <a
                                                    key={i}
                                                    href={item.href}
                                                    className="flex items-center gap-3 px-3 py-2.5 rounded-2xl hover:bg-white/5 transition-colors group"
                                                >
                                                    <item.Icon className="text-purple-400 shrink-0" size={20} />
                                                    <div className="flex flex-col gap-0.5">
                                                        <span className="text-sm text-white font-medium font-poppins group-hover:text-[#8B5CF6] transition-colors whitespace-nowrap">{item.name}</span>
                                                        <span className="text-xs text-gray-400 font-quicksand whitespace-nowrap">{item.description}</span>
                                                    </div>
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center">
                                <LanguageSwitcher />
                            </div>

                            <div className="border-2 border-purple-400/30 p-1 rounded-3xl flex items-center gap-2">
                                <a
                                    href="https://dash.aurex.lk/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center text-[15px] px-4 py-1.5 text-sm text-gray-400 hover:text-white font-poppins transition-colors"
                                >
                                    <HiOutlineSquares2X2 className='mr-2 w-5 h-5 ' /> {t('dashboard')}
                                </a>
                                <a
                                    href="https://dash.aurex.lk/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="pl-1 pr-4 py-1 text-sm font-poppins bg-gradient-to-r from-[#0d3d3a] via-[#0d3d3a]  hover:brightness-110 text-white rounded-full transition-all flex items-center gap-2"
                                >
                                    <Image src="https://cdn.aurex.lk/Ender_Pearl_JE3_BE2.webp" alt="Ender Pearl" width={32} height={32} className="w-8 h-8 object-contain" />
                                    {t('throw')}
                                </a>
                            </div>
                        </div>

                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="md:hidden p-2 text-gray-400 hover:text-white transition-colors"
                            aria-label="Toggle menu"
                        >
                            {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </button>
                    </div>
                </div>

                {isMobileMenuOpen && (
                    <div className="md:hidden border-t border-white/5 backdrop-blur-xl">
                        <div className="px-4 py-4 space-y-4">
                            <div className="space-y-1">
                                <p className="text-[#8B5CF6] text-xs  font-poppins uppercase tracking-wider px-2 mb-2">{t('products')}</p>
                                {productsDropdown.map((item, i) => (
                                    <a
                                        key={i}
                                        href={item.href}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className="flex items-center justify-between px-2 py-2 rounded-md text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                                    >
                                        <span className="text-sm font-poppins">{item.name}</span>
                                        <span className="text-xs text-gray-600 font-quicksand">{item.description}</span>
                                    </a>
                                ))}
                            </div>

                            <div className="border-t border-white/5 pt-4 space-y-1">
                                <p className="text-[#8B5CF6] text-xs  font-poppins uppercase tracking-wider px-2 mb-2">{t('support')}</p>
                                {supportDropdown.map((item, i) => (
                                    <a
                                        key={i}
                                        href={item.href}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className="flex items-center justify-between px-2 py-2 rounded-md text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                                    >
                                        <span className="text-sm font-poppins">{item.name}</span>
                                        <span className="text-xs text-gray-600 font-quicksand">{item.description}</span>
                                    </a>
                                ))}
                            </div>

                            <div className="border-t border-white/5 pt-4 space-y-1">
                                <p className="text-[#8B5CF6] text-xs  font-poppins uppercase tracking-wider px-2 mb-2">{t('social')}</p>
                                {socialDropdown.map((item, i) => (
                                    <a
                                        key={i}
                                        href={item.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className="flex items-center justify-between px-2 py-2 rounded-md text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                                    >
                                        <span className="text-sm font-poppins flex items-center gap-2"><item.Icon className={item.colorClass} /> {item.name}</span>
                                    </a>
                                ))}
                            </div>

                            <div className="border-t border-white/5 pt-4 space-y-1">
                                <p className="text-[#8B5CF6] text-xs  font-poppins uppercase tracking-wider px-2 mb-2">{t('about')}</p>
                                {aboutDropdown.map((item, i) => (
                                    <a
                                        key={i}
                                        href={item.href}
                                        target={item.isExternal ? "_blank" : undefined}
                                        rel={item.isExternal ? "noopener noreferrer" : undefined}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className="flex items-center justify-between px-2 py-2 rounded-md text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                                    >
                                        <span className="text-sm font-poppins">{item.name}</span>
                                        <span className="text-xs text-gray-600 font-quicksand">{item.description}</span>
                                    </a>
                                ))}
                            </div>

                            <div className="border-t border-white/5 pt-4 space-y-2">
                                <a href="https://status.aurex.lk/" target="_blank" rel="noopener noreferrer" onClick={() => setIsMobileMenuOpen(false)} className="block px-2 py-2 text-sm text-gray-300 hover:text-white font-poppins transition-colors">
                                    {t('status')}
                                </a>
                                <a
                                    href="https://dash.aurex.lk/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="w-full mt-1 px-4 py-2.5 text-sm font-poppins bg-gradient-to-r from-[#0d3d3a] via-[#1a7a6e] to-[#2aaa82] hover:brightness-110 text-white rounded-2xl transition-all flex items-center justify-center gap-2"
                                >
                                    <Image src="https://cdn.aurex.lk/Ender_Pearl_JE3_BE2.webp" alt="Ender Pearl" width={32} height={32} className="w-8 h-8 object-contain" />
                                    {t('get_started')}
                                </a>
                            </div>
                        </div>
                    </div>
                )}
            </nav>
        </div>
    )
}

export default Navbar
